import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, Loader2, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import * as api from '../api';
import { toast } from 'sonner';
import { useSociete } from '../context/SocieteContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtEur(v) {
  return (v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
function fmtDate(s) {
  if (!s) return '—';
  const d = s.split('T')[0].split('-');
  return `${d[2]}/${d[1]}/${d[0]}`;
}

const MODULE_CFG = {
  vente:    { label: 'Vente',    dot: '#3B82F6' },
  location: { label: 'Location', dot: '#10B981' },
  atelier:  { label: 'Atelier',  dot: '#F59E0B' },
};

const PL_SYNC_CFG = {
  synced:         { label: 'Synchronisé', color: '#15803D' },
  pending:        { label: 'En attente',  color: '#B45309' },
  a_synchroniser: { label: 'À synchro',   color: '#C2410C' },
};

function ModuleDot({ module }) {
  const cfg = MODULE_CFG[module] || { label: module, dot: '#94A3B8' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

const TH = { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
             color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em',
             background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', whiteSpace: 'nowrap' };
const TD = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #F1F5F9', color: '#1E293B' };

const MODULES = [
  { key: '',         label: 'Tous' },
  { key: 'vente',    label: 'Vente' },
  { key: 'location', label: 'Location' },
  { key: 'atelier',  label: 'Atelier' },
];

export default function ReglementsClient() {
  const { activeAgences } = useSociete();

  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [modes, setModes]         = useState([]);
  const [resyncing, setResyncing] = useState(false);

  const [module, setModule]   = useState('');
  const [mode,   setMode]     = useState('');
  const [dateD,  setDateD]    = useState('');
  const [dateF,  setDateF]    = useState('');
  const [search, setSearch]   = useState('');

  const PER_PAGE = 50;

  // Charger les modes de règlement
  useEffect(() => {
    api.getModesReglement().then(r => setModes(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: PER_PAGE };
      if (module)       params.module     = module;
      if (mode)         params.mode       = mode;
      if (dateD)        params.date_debut = dateD;
      if (dateF)        params.date_fin   = dateF;
      if (search.trim()) params.client_q  = search.trim();
      if (activeAgences?.length) params.agence_ids = activeAgences.map(a => a.agence_id).join(',');
      const res = await api.getReglementsUnifies(params);
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch {
      toast.error('Erreur chargement règlements');
    } finally {
      setLoading(false);
    }
  }, [module, mode, dateD, dateF, search, activeAgences]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(1); }, [module, mode, dateD, dateF, activeAgences]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResyncPending = async () => {
    setResyncing(true);
    try {
      const res = await api.resyncPendingReglements({});
      const { total: nb, synced, skipped, errors } = res.data;
      if (nb === 0) {
        toast.success('Aucun règlement en attente à synchroniser');
      } else if (synced > 0) {
        toast.success(`${synced} règlement${synced > 1 ? 's' : ''} synchronisé${synced > 1 ? 's' : ''}${errors ? ` · ${errors} erreur(s)` : ''}`);
      } else if (errors > 0) {
        toast.error(`${errors} erreur(s) lors de la synchronisation`);
      } else {
        toast.info(`${nb} règlement${nb > 1 ? 's' : ''} traité${nb > 1 ? 's' : ''} — ${skipped} ignoré${skipped > 1 ? 's' : ''} (clé API manquante ou facture non validée)`);
      }
      load(page);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la re-synchronisation');
    } finally {
      setResyncing(false);
    }
  };

  const handleSearch = (e) => { e.preventDefault(); load(1); };

  const totalPages = Math.ceil(total / PER_PAGE);

  // Totaux de la page courante
  const totalPage = rows.reduce((s, r) => s + r.montant, 0);

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* En-tête */}
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0F172A', margin: 0 }}>Règlements client</h1>
          <p style={{ fontSize: 13, color: '#64748B', margin: '4px 0 0' }}>
            {total.toLocaleString('fr-FR')} règlement{total !== 1 ? 's' : ''} — Ventes · Location · Atelier
          </p>
        </div>

        {/* Bouton re-sync Pennylane */}
        <button
          data-testid="btn-resync-pending"
          onClick={handleResyncPending}
          disabled={resyncing}
          title="Re-synchronise vers Pennylane tous les règlements bloqués en statut 'En attente' dont la facture est finalisée"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: resyncing ? 'default' : 'pointer',
            border: '1px solid #E2E8F0', background: resyncing ? '#F1F5F9' : '#fff',
            color: resyncing ? '#94A3B8' : '#374151', transition: 'all .15s',
            boxShadow: '0 1px 2px rgba(0,0,0,.05)',
          }}
          onMouseEnter={e => { if (!resyncing) e.currentTarget.style.borderColor = '#CBD5E1'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; }}
        >
          <RefreshCw size={13} style={resyncing ? { animation: 'spin 1s linear infinite' } : {}} />
          {resyncing ? 'Synchronisation…' : 'Sync Pennylane'}
        </button>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        {/* Module pills */}
        <div style={{ display: 'flex', gap: 4, background: '#F1F5F9', borderRadius: 8, padding: 3 }}>
          {MODULES.map(m => (
            <button key={m.key}
              data-testid={`filter-module-${m.key || 'tous'}`}
              onClick={() => setModule(m.key)}
              style={{
                padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                border: 'none', cursor: 'pointer', transition: 'all .15s',
                background: module === m.key ? '#fff' : 'transparent',
                color: module === m.key ? '#0F172A' : '#64748B',
                boxShadow: module === m.key ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
              }}>
              {m.key && <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                background: MODULE_CFG[m.key]?.dot, marginRight: 5, verticalAlign: 'middle' }} />}
              {m.label}
            </button>
          ))}
        </div>

        {/* Mode de règlement */}
        <select value={mode} onChange={e => setMode(e.target.value)}
          data-testid="filter-mode"
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
            fontSize: 12, color: '#374151', background: '#fff', cursor: 'pointer' }}>
          <option value="">Tous modes</option>
          {modes.map(m => <option key={m.code} value={m.code}>{m.libelle}</option>)}
        </select>

        {/* Dates */}
        <input type="date" value={dateD} onChange={e => setDateD(e.target.value)}
          data-testid="filter-date-debut"
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 12, color: '#374151' }} />
        <input type="date" value={dateF} onChange={e => setDateF(e.target.value)}
          data-testid="filter-date-fin"
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 12, color: '#374151' }} />

        {/* Recherche client */}
        <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un client…"
              data-testid="filter-client-search"
              style={{ paddingLeft: 28, paddingRight: 8, paddingTop: 5, paddingBottom: 5,
                borderRadius: 6, border: '1px solid #E2E8F0', fontSize: 12, width: 200 }} />
          </div>
          {search && (
            <button type="button" onClick={() => { setSearch(''); load(1); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}>
              <X size={13} />
            </button>
          )}
          <button type="submit"
            style={{ padding: '5px 12px', borderRadius: 6, border: 'none',
              background: '#1E293B', color: '#fff', fontSize: 12, cursor: 'pointer' }}>
            Chercher
          </button>
        </form>

        {/* Reset */}
        {(module || mode || dateD || dateF || search) && (
          <button onClick={() => { setModule(''); setMode(''); setDateD(''); setDateF(''); setSearch(''); }}
            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
              fontSize: 12, background: '#fff', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={11} /> Réinitialiser
          </button>
        )}
      </div>

      {/* Tableau */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60, color: '#94A3B8' }}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} />
            Chargement…
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8', fontSize: 13 }}>
            Aucun règlement
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>Date règlement</th>
                  <th style={TH}>Client</th>
                  <th style={TH}>N° Facture</th>
                  <th style={TH}>Module</th>
                  <th style={TH}>Mode</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Montant</th>
                  <th style={TH}>Référence</th>
                  <th style={TH}>Statut PL</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.reglement_id}
                    data-testid={`reglement-row-${r.reglement_id}`}
                    style={{ transition: 'background .1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={TD}>{fmtDate(r.date_reglement)}</td>
                    <td style={TD}>{r.entreprise_nom}</td>
                    <td style={{ ...TD, color: '#475569', fontFamily: 'monospace', fontSize: 12 }}>{r.ext_invoice_number}</td>
                    <td style={TD}><ModuleDot module={r.module} /></td>
                    <td style={TD}>{r.mode_libelle}</td>
                    <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtEur(r.montant)}</td>
                    <td style={{ ...TD, color: '#64748B', fontSize: 12 }}>{r.reference || '—'}</td>
                    <td style={{ ...TD, fontSize: 12, color: PL_SYNC_CFG[r.pl_sync_status]?.color || '#94A3B8' }}>
                      {PL_SYNC_CFG[r.pl_sync_status]?.label || r.pl_sync_status || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Ligne total page */}
              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#F8FAFC', borderTop: '2px solid #E2E8F0' }}>
                    <td colSpan={5} style={{ ...TD, fontWeight: 700, fontSize: 12, color: '#475569' }}>
                      Total — {rows.length} règlement{rows.length !== 1 ? 's' : ''} (page courante)
                    </td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtEur(totalPage)}
                    </td>
                    <td colSpan={2} style={TD} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderTop: '1px solid #F1F5F9', background: '#F8FAFC' }}>
            <span style={{ fontSize: 12, color: '#64748B' }}>
              Page {page} / {totalPages} — {total.toLocaleString('fr-FR')} résultats
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => load(page - 1)} disabled={page <= 1}
                data-testid="pagination-prev"
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
                  background: page <= 1 ? '#F1F5F9' : '#fff', cursor: page <= 1 ? 'default' : 'pointer',
                  color: page <= 1 ? '#CBD5E1' : '#374151', display: 'flex', alignItems: 'center' }}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => load(page + 1)} disabled={page >= totalPages}
                data-testid="pagination-next"
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #E2E8F0',
                  background: page >= totalPages ? '#F1F5F9' : '#fff', cursor: page >= totalPages ? 'default' : 'pointer',
                  color: page >= totalPages ? '#CBD5E1' : '#374151', display: 'flex', alignItems: 'center' }}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
